/**
 * Stage In capture helper.
 *
 * Uses navigator.mediaDevices.getDisplayMedia (Zoom "Share computer sound" model).
 * We request video too because some browsers require it for system-audio capture,
 * but we immediately drop the video tracks — we only ever broadcast the audio.
 *
 * Music-grade audio constraints: NO echo cancellation, NO noise suppression, NO AGC.
 */

export function isMac() {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform) ||
         /Mac OS X/.test(navigator.userAgent);
}

export function isSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // Chrome/Edge/Firefox all advertise "Safari" too; the canonical sniff is:
  return /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
}

export function isFirefox() {
  if (typeof navigator === "undefined") return false;
  return /Firefox/i.test(navigator.userAgent);
}

/** Touch-first device sniff. Catches iOS Safari, Android Chrome, mobile Firefox,
 * iPad Safari (incl. desktop-mode iPad which UA-spoofs as Mac — we additionally
 * check `maxTouchPoints` to catch that one).
 *
 * Mobile browsers DO NOT expose `getDisplayMedia` with system-audio capture:
 * iOS/iPadOS Safari rejects the call entirely; Android Chrome only exposes
 * tab-capture without audio. There is no path to ship Stage In on mobile web —
 * native audio capture lands in Phase 5 (Expo/Electron).
 */
export function isMobile() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true;
  if (/iPad/i.test(ua)) return true;
  // iPadOS 13+ in desktop-mode UA-spoofs Mac; touch-points outs it.
  if (/Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1) return true;
  return false;
}

/** Stage In requires getDisplayMedia. Safari on macOS doesn't surface system audio at all.
 * Mobile browsers can't capture system audio under any circumstance. */
export function isStageInSupported() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getDisplayMedia) return false;
  if (isMobile()) return false;
  if (isMac() && isSafari()) return false;
  // Firefox on every OS can't capture system audio either.
  if (isFirefox()) return false;
  return true;
}

/**
 * Try to capture system / app audio from the picker. Returns:
 *   { stream, audioTrack, label, stop() }
 * Throws on user denial or no-audio-track-selected.
 */
export async function captureStageInAudio() {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error("UNSUPPORTED_BROWSER");
  }
  // Some Chromium builds reject the call unless `video: true`.
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: true,
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
  });

  // Drop the video — we only use audio.
  for (const v of stream.getVideoTracks()) {
    try { v.stop(); } catch { /* ignore */ }
    try { stream.removeTrack(v); } catch { /* ignore */ }
  }

  const audioTrack = stream.getAudioTracks()[0];
  if (!audioTrack) {
    for (const t of stream.getTracks()) { try { t.stop(); } catch { /* ignore */ } }
    throw new Error("NO_AUDIO_TRACK_SELECTED");
  }

  const label = audioTrack.label || "Captured audio";

  const stop = () => {
    for (const t of stream.getTracks()) { try { t.stop(); } catch { /* ignore */ } }
  };

  return { stream, audioTrack, label, stop };
}
